import { TokenDetailsFragment } from '@/gql/generated/graphql'
import { AddressDisplay } from '@components/address-display'
import { LoadingIndicator } from '@components/loading-indicator'
import { AcceptBidModal, useBids } from '@reservoir0x/reservoir-kit-ui'
import { getReservoirCollectionId, getReservoirTokenId } from '@utils/reservoir'
import { BodyText, HandArrowUpIcon } from 'design-configuration/components'
import { AnimatePresence, motion } from 'motion/react'
import { Hex } from 'viem'
import { formatDate, getExpirationString } from '../utils'
import { TokenInfoListItem } from './token-info-list-item'
import { useCallback, useMemo } from 'react'
import { ALLOWED_LIST_PLATFORMS } from '@utils/common'
import { useSession } from 'next-auth/react'
import { cn, formatUSD } from '@/lib/utils'
import { AllPurposeLink } from '@components/all-purpose-link'
import { marketplaceUserOffersRoute } from '@utils/routes'
import { ScrollSentinel } from '@components/scroll-sentinel'

export function Offers({ token }: { token: TokenDetailsFragment }): React.ReactElement {
  const { data: session } = useSession()
  const isOwner = session?.user?.publicAddress?.toLowerCase() === token.owner_address?.toLowerCase()
  const {
    data: allOffers,
    isFetchingInitialData,
    isFetchingPage,
    fetchNextPage,
    hasNextPage
  } = useBids({
    token: getReservoirTokenId(token.id),
    status: 'active',
    sortBy: 'price'
  })

  const offers = useMemo(() => {
    return allOffers?.filter((offer) => {
      return ALLOWED_LIST_PLATFORMS.includes((offer.source as { name: string } | undefined)?.name ?? '')
    })
  }, [allOffers])

  const handleScrollSentinelInViewChange = useCallback(
    (inView: boolean) => {
      if (inView && hasNextPage && !isFetchingPage && !isFetchingInitialData) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingPage, isFetchingInitialData]
  )
  return (
    <div className="p-sm h-full">
      <div className="relative h-full min-h-20">
        <AnimatePresence>
          {isFetchingInitialData ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-md absolute inset-0 flex justify-center"
            >
              <LoadingIndicator className="w-lg h-lg fill-outline-variant" />
            </motion.div>
          ) : (
            <motion.div
              className="gap-xs flex h-full flex-col"
              key="token-events"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {offers?.length ? (
                <AnimatePresence>
                  {offers?.map((event) => {
                    // Bid
                    const isBidder = event.maker.toLowerCase() === session?.user?.publicAddress?.toLowerCase()
                    const expirationString = event.expiration
                      ? getExpirationString(new Date(event.expiration * 1000))
                      : null

                    if (expirationString === 'Expired' || !expirationString) {
                      return null
                    }

                    const offerElem = (
                      <TokenInfoListItem
                        className={cn(isOwner || isBidder ? 'hover:border-outline cursor-pointer' : '')}
                        topLeft={
                          <span className="gap-2xs flex items-center">
                            <HandArrowUpIcon className="fill-on-surface-variant" /> Offer By
                          </span>
                        }
                        topRight={
                          <>
                            <span>{event.createdAt ? formatDate(event.createdAt) : null}</span>{' '}
                            <span className="detail-text font-normal normal-case">{expirationString}</span>
                          </>
                        }
                        bottomLeft={<AddressDisplay address={event.maker as Hex | undefined} />}
                        bottomRight={
                          event.price ? (
                            <>
                              {event.price.amount?.decimal} {event.price.currency?.symbol}{' '}
                              <span className="text-detail normal-case">
                                ${formatUSD(event.price.amount?.usd ?? 0)} USD
                              </span>
                            </>
                          ) : null
                        }
                      />
                    )
                    return (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        key={JSON.stringify(event)}
                      >
                        {isOwner ? (
                          <AcceptBidModal
                            trigger={<button className="w-full flex-1">{offerElem}</button>}
                            tokens={[
                              {
                                tokenId: token.token_id,
                                bidIds: [event.id],
                                collectionId: getReservoirCollectionId(token.contract_address, token.project.project_id)
                              }
                            ]}
                          />
                        ) : isBidder ? (
                          <AllPurposeLink
                            href={marketplaceUserOffersRoute(event.maker?.toLowerCase() ?? '')}
                            target="_blank"
                          >
                            <button className="w-full flex-1">{offerElem}</button>
                          </AllPurposeLink>
                        ) : (
                          offerElem
                        )}
                      </motion.div>
                    )
                  })}
                  {isFetchingPage && (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="p-md flex w-full justify-center"
                    >
                      <LoadingIndicator className="w-lg h-lg fill-outline-variant" />
                    </motion.div>
                  )}
                </AnimatePresence>
              ) : (
                <div className="gap-xs text-on-surface-variant flex h-full w-full flex-col items-center justify-center py-[108px]">
                  <BodyText className="font-medium">No offers to display</BodyText>
                  <BodyText>This artwork does not have any active offers.</BodyText>
                </div>
              )}
            </motion.div>
          )}
          <ScrollSentinel
            onInViewChange={handleScrollSentinelInViewChange}
            useInViewOptions={{
              margin: '50px',
              initial: true
            }}
          />
        </AnimatePresence>
      </div>
    </div>
  )
}
